<template>
  <div class="cinema-roll">
    <!-- Invisible scroll-to-top trigger area -->
    <div class="scroll-to-top-trigger" @click.stop="scrollToTop"></div>
    <AppHeader/>
    <RouteProgress/>
    <SavedFlash/>
    <!-- UpdateAvailableBanner and BugResolutionNotice render inside Home's
         .home-notices section now — the one notification space — not here.
         The auto-update machinery below stays global; only the visible
         fallback moved. Offline and access banners remain global: they are
         states of the whole app, not notifications. -->
    <OfflineBanner/>
    <LibraryAccessBanner/>
    <!-- The routed page grows to fill whatever is left below the header, which
         is what keeps the footer at the bottom. Pages used to each declare
         `min-height: 100vh` for that, which made every page header + a FULL
         viewport + footer tall — so there was always a screenful of empty
         space to scroll through. -->
    <main class="app-main">
      <!-- Enter-only fade: the new screen eases in over 140ms; nothing is
           delayed (no leave phase), the arrival just stops being a cut. -->
      <router-view v-slot="slot">
        <transition name="screen" appear>
          <component :is="slot && slot.Component"/>
        </transition>
      </router-view>
    </main>
    <AppFooter v-if="$store.state.dbLoaded"/>
    <BugReportButton/>
  </div>
</template>

<script>
import AppFooter from "./components/Footer.vue";
import AppHeader from "./components/Header.vue";
import BugReportButton from "./components/BugReportButton.vue";
import OfflineBanner from "./components/OfflineBanner.vue";
import LibraryAccessBanner from "./components/LibraryAccessBanner.vue";
import RouteProgress from "./components/RouteProgress.vue";
import SavedFlash from "./components/SavedFlash.vue";
import { pickFallbackBanner } from "./assets/javascript/bannerFallback.js";
import { flushStashedBugReports } from "./utils/bugReports.js";
import { reloadForUpdate, watchForStalledReload, isSafeMomentForReload, shouldAutoAttempt, markUpdateLanded, recordWorkerState, checkWorkerOnce, isFreshMoment, runUpdateCheck, QUIET_MS } from "./utils/appUpdate.js";
import { fetchWithTimeout } from "./utils/networkHealth.js";
import { refreshSubscriptionIfGranted, closeDeliveredNotifications } from "./utils/push.js";

export default {
  name: "Cinema-Roll",
  components: {
    RouteProgress,
    SavedFlash,
    AppFooter,
    AppHeader,
    BugReportButton,
    LibraryAccessBanner,
    OfflineBanner
  },
  data () {
    return {
      lastActivityAt: Date.now(),
      // A real touch or keypress, as opposed to any activity (a scroll can be
      // the page restoring its own position). Decides "right away" vs "wait
      // for quiet" for an update (appUpdate.js isFreshMoment).
      lastTouchAt: 0,
      lastBecameVisibleAt: Date.now(),
      deployedBundleSeen: null,
      autoUpdateTimer: null,
      cancelStallWatch: null,
      pushDigestTimer: null
    };
  },
  computed: {
    libraryCount () {
      return Object.keys(this.$store.state.movieLog || {}).length;
    }
  },
  watch: {
    // Auto-apply updates (bug report: "the user shouldn't have to take an
    // action... the banner could still be a fallback"). Immediately when
    // the update is spotted at a fresh moment (just launched/foregrounded —
    // nothing is in progress yet), otherwise after ~8s of no interaction —
    // and never while typing, mid-game, or in a modal (the July lesson:
    // an unconditional reload yanked the page out from under a backfill).
    '$store.state.updateAvailable' (available) {
      if (!available) return;
      this.armAutoUpdate();
    },
    // A new service worker was seen. That alone is not an update (a worker
    // stuck in `waiting` reports itself on every launch, 2026-09-29 loop) -
    // only a real bundle mismatch is.
    '$store.state.updateCheckRequests' () {
      this.checkDeployedBundle();
    },
    // Cold boot on a non-home route: Home (the usual banner resolver) never
    // mounts, so once the library arrives, pick the fallback banner here.
    // Home still owns banner choice whenever it runs — this only fills a
    // banner nothing else has set.
    libraryCount (count) {
      if (!count || this.$store.state.bannerUrl) return;
      const url = pickFallbackBanner(this.$store.getters.allMoviesAsArray);
      if (url && !this.$store.state.bannerUrl) {
        this.$store.commit('setBannerUrl', url);
      }
    },
    // Push notifications: once the library is in, load prefs/subscriptions,
    // self-heal this device's subscription, and publish the first digest.
    '$store.state.dbLoaded' (loaded) {
      if (loaded) this.initializePush();
    },
    // Any library or settings change can change what the daily push should
    // say (a rating added, stickiness recorded, a tournament finished, a
    // prompt disabled) — republish the digest, debounced. Both mutations
    // replace the object reference, so shallow watchers are enough.
    '$store.state.movieLog' () {
      this.schedulePushDigest();
    },
    '$store.state.settings' () {
      this.schedulePushDigest();
    },
    // The icon badge can count Showtimes films (a Notifications switch, off
    // by default): recount when the switch flips, the board arrives, or a
    // film is dismissed or snoozed. refreshAppBadge no-ops the films part
    // unless the switch is on.
    '$store.state.pushPrefs' () {
      this.$store.dispatch('refreshAppBadge');
    },
    '$store.state.theaterBoard' () {
      this.$store.dispatch('refreshAppBadge');
    },
    '$store.state.theaterDismissed' () {
      this.$store.dispatch('refreshAppBadge');
    },
    '$store.state.theaterReminders' () {
      this.$store.dispatch('refreshAppBadge');
    },
    // Friend requests count on the icon too: recount when one arrives or is
    // answered (accepting changes the edges, not just the requests).
    '$store.state.socialRequests' () {
      this.$store.dispatch('refreshAppBadge');
    },
    '$store.state.socialEdges' () {
      this.$store.dispatch('refreshAppBadge');
    }
  },
  methods: {
    async initializePush () {
      // Opening the app sets the icon badge to the chores actually waiting
      // (it used to clear it, which left the icon blank with work to do).
      this.$store.dispatch('refreshAppBadge');
      closeDeliveredNotifications();
      await this.$store.dispatch('loadPushState');
      await refreshSubscriptionIfGranted(this.$store);
      this.$store.dispatch('publishPushDigest');
    },
    schedulePushDigest () {
      // Trailing-edge debounce: a backfill or delta sync can replace the
      // library many times in a burst, and one digest at the end is worth
      // the same as fifty along the way.
      clearTimeout(this.pushDigestTimer);
      this.pushDigestTimer = setTimeout(() => {
        this.pushDigestTimer = null;
        this.$store.dispatch('publishPushDigest');
        this.$store.dispatch('publishPeopleList');
        this.$store.dispatch('refreshAppBadge');
      }, 5000);
    },
    // Going away: publish a pending digest NOW. A backgrounded PWA does not
    // run setTimeout, so a change made in the last five seconds before the
    // phone went in a pocket never reached the server. Bug report
    // (2026-10-05): a tiebreak settled at 3:09 restarted the tiebreak quota,
    // the digest saying so never left the phone, and at 3:50 the sweep
    // announced a tie from the pre-tiebreak copy - which Home, honouring the
    // quota, then refused to show. A no-op when nothing is pending.
    flushPushDigest () {
      if (!this.pushDigestTimer) return;
      clearTimeout(this.pushDigestTimer);
      this.pushDigestTimer = null;
      this.$store.dispatch('publishPushDigest');
    },
    scrollToTop () {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    },
    async checkForServiceWorkerUpdate () {
      if (!('serviceWorker' in navigator)) {
        return;
      }

      // An update already spotted but not applied (deferred on a weak
      // connection, or the first try didn't land): coming back to the app is
      // a fresh moment, so give it its second automatic try.
      if (this.$store.state.updateAvailable && !this.$store.state.updateApplying && !this.autoUpdateTimer) {
        this.armAutoUpdate();
      }

      // Side by side, the worker's half capped (appUpdate.js runUpdateCheck):
      // a failed or slow worker check just means we try again on the next
      // trigger, and must never hold up spotting a new deploy.
      await runUpdateCheck({
        refreshWorker: async () => {
          const registration = await navigator.serviceWorker.getRegistration();
          recordWorkerState(registration);
          if (registration) {
            // Shared with the refresh that may follow (appUpdate.js
            // checkWorkerOnce), so it joins this check instead of queueing
            // a second one behind it.
            await checkWorkerOnce(registration);
            recordWorkerState(registration);
          }
        },
        checkBundle: () => this.checkDeployedBundle()
      });
    },
    /**
     * Notices a new deploy by comparing bundle filenames, independent of any
     * service worker state.
     *
     * Bug report: "I didn't get my refresh for new version banner." The
     * banner is driven by registerServiceWorker's `updated()` hook, which
     * only fires while a new worker sits in the `installed` state — but this
     * app builds with `skipWaiting: true` (vue.config.js), so a new worker
     * activates itself immediately instead of waiting. That makes the hook a
     * race the banner often loses: you get the new version, you just don't
     * get told. Comparing what the server serves against what this page
     * actually loaded doesn't depend on that timing at all.
     *
     * The cache-busting param matters: Workbox precaches index.html, and
     * only strips params matching `ignoreURLParametersMatching` (utm_,
     * fbclid). An arbitrary param therefore misses the precache entry and
     * goes to the network, which is the point.
     */
    async checkDeployedBundle () {
      if (this.$store.state.updateAvailable) {
        return;
      }

      const runningBundle = this.currentBundleName();
      if (!runningBundle) {
        return;
      }

      try {
        const response = await fetchWithTimeout(`${process.env.BASE_URL || '/'}index.html?updateCheck=${Date.now()}`, { cache: 'no-store' });
        if (!response.ok) {
          return;
        }
        const deployedBundle = (await response.text()).match(/js\/app\.[a-z0-9]+\.js/);
        if (deployedBundle && deployedBundle[0] !== runningBundle) {
          this.deployedBundleSeen = deployedBundle[0];
          this.$store.commit('setUpdateTargetBundle', deployedBundle[0]);
          this.$store.commit('setUpdateAvailable', true);
        } else if (deployedBundle) {
          // Running what's deployed: any earlier reload attempt worked, so
          // the next update starts from a clean slate (appUpdate.js).
          markUpdateLanded();
        }
      } catch {
        // Offline, blocked, or the check simply failed - try again next time.
      }
    },
    /** The app bundle THIS page is running, read off its own script tags. */
    currentBundleName () {
      const scripts = Array.from(document.querySelectorAll('script[src]'));
      for (const script of scripts) {
        const match = (script.getAttribute('src') || '').match(/js\/app\.[a-z0-9]+\.js/);
        if (match) return match[0];
      }
      return null;
    },
    // Offline rating support: attempt a queue flush whenever we might have
    // just regained connectivity. Cheap/no-op if actually still offline
    // (flushPendingWrites checks navigator.onLine itself), so it's safe to
    // call from every trigger below rather than just the 'online' event -
    // same reasoning as checkForServiceWorkerUpdate needing four independent
    // triggers instead of trusting any single browser event.
    attemptPendingWritesFlush () {
      this.$store.dispatch('flushPendingWrites');
    },
    handleOnline () {
      this.$store.commit('setIsOnline', true);
      this.attemptPendingWritesFlush();
      // Bug reports live outside the account-scoped queue; drain their own
      // localStorage stash on reconnect (2026-08-15 offline audit).
      flushStashedBugReports().catch(() => {});
    },
    handleOffline () {
      this.$store.commit('setIsOnline', false);
    },
    noteActivity (event) {
      this.lastActivityAt = Date.now();
      if (event?.type !== 'scroll') this.lastTouchAt = this.lastActivityAt;
    },
    async applyUpdate () {
      // The banner reads this to say "Updating…" instead of offering a
      // Refresh that would only get in the way.
      this.$store.commit('setUpdateApplying', true);
      const startedAt = Date.now();
      const outcome = await reloadForUpdate({
        target: this.deployedBundleSeen,
        sinceVisibleMs: startedAt - (this.lastBecameVisibleAt || 0),
        // Quiet when it began isn't quiet now (2026-10-09: the wait took 9
        // seconds and the reload landed mid-dismissing on Showtimes).
        stillQuiet: () => this.lastTouchAt <= startedAt && isSafeMomentForReload({ routePath: this.$route?.path || '' })
      });
      if (outcome === 'deferred') {
        this.$store.commit('setUpdateApplying', false);
        this.$store.commit('setUpdateDeferred', true);
      } else if (outcome === 'interrupted') {
        // Back to the banner, and try again after a quiet stretch.
        this.$store.commit('setUpdateApplying', false);
        this.applyWhenQuiet();
      } else if (outcome === 'reloaded' || outcome === 'hard') {
        // Still here well after asking to go: the reload never happened
        // (2026-10-14, stuck on "Updating…" for 47 minutes). Hand the notice
        // back and try again - the same version again, so this time hard.
        if (this.cancelStallWatch) this.cancelStallWatch();
        this.cancelStallWatch = watchForStalledReload(() => {
          this.cancelStallWatch = null;
          this.$store.commit('setUpdateApplying', false);
          this.armAutoUpdate();
        });
      }
    },
    armAutoUpdate () {
      const target = this.deployedBundleSeen || 'unknown';
      if (!shouldAutoAttempt(target)) return; // twice per version; then the banner

      // Fresh moment (just launched or foregrounded, and not touched since):
      // nothing is in flight yet — apply right away.
      const fresh = isFreshMoment({ becameVisibleAt: this.lastBecameVisibleAt || 0, lastTouchAt: this.lastTouchAt });
      if (fresh && isSafeMomentForReload({ routePath: this.$route?.path || '' })) {
        this.applyUpdate();
        return;
      }

      this.applyWhenQuiet();
    },
    // Poll for a quiet stretch, then apply.
    applyWhenQuiet () {
      if (this.autoUpdateTimer) clearInterval(this.autoUpdateTimer);
      this.autoUpdateTimer = setInterval(() => {
        const quiet = Date.now() - this.lastActivityAt > QUIET_MS;
        if (quiet && isSafeMomentForReload({ routePath: this.$route?.path || '' })) {
          clearInterval(this.autoUpdateTimer);
          this.autoUpdateTimer = null;
          this.applyUpdate();
        }
      }, 2000);
    }
  },
  async mounted () {
    // Reconcile the router's localStorage-based "you're signed in" assumption
    // against whether Firebase actually restored a session. Fire and forget —
    // it resolves itself once auth settles and must not delay first paint.
    this.$store.dispatch('verifyRestoredSession');

    // The dbLoaded watcher handles the normal boot order; this covers the
    // library having loaded before this component mounted.
    if (this.$store.state.dbLoaded) this.initializePush();

    // visibilitychange alone is unreliable on iOS, particularly for a
    // home-screen-installed PWA - it's a long-standing WebKit quirk that it
    // sometimes just doesn't fire when the app is brought back to the
    // foreground (varies by iOS version, which is why this can silently work
    // on one iPhone and never on another). pageshow and window focus are
    // more consistent there, and the interval is a backstop that doesn't
    // depend on any lifecycle event firing at all - between the three, an
    // update gets picked up even if any single trigger fails to fire.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.lastBecameVisibleAt = Date.now();
        this.checkForServiceWorkerUpdate();
        this.attemptPendingWritesFlush();
        // Foregrounding an installed PWA doesn't re-run initializePush.
        // Recount rather than clear: a friend-log push's extra one drops,
        // the chores still waiting stay on the icon.
        this.$store.dispatch('refreshAppBadge');
        closeDeliveredNotifications();
        // Awards changed on another device: a few bytes to find out.
        this.$store.dispatch('ensurePersonalAwards');
      } else {
        // Leaving: the icon should show what's left, as of now.
        this.$store.dispatch('refreshAppBadge');
        // Going away: run any pending debounced profile publish before the
        // timer dies with the page. See scheduleSocialPublish — a backgrounded
        // PWA does not run setTimeout, so this is the difference between a
        // friend seeing today's rating and seeing it six hours from now.
        this.$store.dispatch('flushSocialPublish');
        this.$store.dispatch('flushMirrorPublish');
        this.flushPushDigest();
      }
    });
    // pagehide is the reliable half of this on iOS (same reasoning as the
    // comment above, in the other direction). Both are no-ops when nothing is
    // pending, so firing on both costs nothing.
    window.addEventListener('pagehide', () => {
      this.$store.dispatch('flushSocialPublish');
      this.$store.dispatch('flushMirrorPublish');
      this.flushPushDigest();
    });
    window.addEventListener('pageshow', () => {
      this.lastBecameVisibleAt = Date.now();
      this.checkForServiceWorkerUpdate();
      this.attemptPendingWritesFlush();
    });
    window.addEventListener('focus', () => {
      this.checkForServiceWorkerUpdate();
      this.attemptPendingWritesFlush();
    });
    // Activity signals for the auto-update quiet-moment detector. Passive:
    // these must never delay scrolling.
    ['pointerdown', 'keydown', 'wheel', 'touchstart', 'scroll'].forEach((eventName) => {
      window.addEventListener(eventName, this.noteActivity, { passive: true });
    });
    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);
    setInterval(() => {
      this.checkForServiceWorkerUpdate();
      this.attemptPendingWritesFlush();
    }, 30 * 60 * 1000);
  },
}
</script>

<style lang="scss">
  body {
    font-family: "Roboto Condensed", sans-serif;

    &.no-scroll {
      overflow: hidden;
    }
  }

  .cinema-roll {
    min-height: 100vh;
    width: 100%;
    max-width: 100%;
    // Clip any stray horizontal overflow so the page can never scroll sideways.
    // On the installed iOS PWA a brief horizontal overflow (e.g. an image
    // rendering at natural width before CSS constrains it) latches the layout
    // viewport wider than the screen; that stuck viewport then makes every
    // screen's 100vh-based vertical layout stop a few % short of the bottom
    // (cutting off the submit / poster buttons) until the app is force-quit.
    // Clipping here prevents the latch — fixes the sideways scroll AND the
    // cumulative bottom cut-off together. Must be clip, not hidden: hidden
    // forces overflow-y to compute to auto, which makes this wrapper the
    // sticky containing scrollport and silently breaks every position:sticky
    // descendant (the page scrolls, this element never does). clip clips
    // harder (no programmatic scroll either — better for the latch) without
    // creating a scroll container. hidden stays as an old-engine fallback.
    overflow-x: hidden;
    overflow-x: clip;
    display: flex;
    flex-direction: column;
  }

  /* This replaces a `.cinema-roll > router-view` rule that never matched
     anything: <router-view> renders the matched component IN ITS PLACE, so no
     `router-view` element exists in the DOM. */
  .app-main {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    width: 100%;
  }

  /* The page itself stretches to fill, rather than demanding a viewport of its
     own. Same visual result, no phantom scroll. */
  .app-main > * {
    flex: 1 1 auto;
  }

  .scroll-to-top-trigger {
    position: fixed;
    top: 0;
    left: 75px;
    right: 75px;
    height: 40px;
    z-index: 9999;
    background: transparent;
    cursor: pointer;
    -webkit-tap-highlight-color: rgba(255, 255, 255, 0.1);
    touch-action: manipulation;

    /* Only show on mobile devices */
    @media (min-width: 768px) {
      display: none;
    }
  }
</style>

<style>
/* Feel-of-response rules (2026-09-23), app-wide and deliberately quiet.
   Bootstrap's reboot removes the native tap highlight, so custom tappable
   things gave NO sign a finger landed until their screen changed. Every
   custom tap target in the app declares .tap-feedback (or its own
   :active), which presses in by 2% and dims for as long as the finger is
   down. Buttons keep Bootstrap's own :active colours. */
.tap-feedback {
  transition: transform 90ms ease-out, opacity 90ms ease-out, filter 90ms ease-out;
}

.tap-feedback:active {
  transform: scale(0.98);
  opacity: 0.82;
}

/* A poster arriving: the card already shows a placeholder while the real
   image loads (vue3-lazyload swaps `lazy` from loading to loaded); this
   just keeps the swap from being a hard cut. */
img[lazy="loading"] {
  filter: brightness(0.75);
}

img[lazy="loaded"] {
  animation: poster-arrive 220ms ease-out;
}

@keyframes poster-arrive {
  from { filter: brightness(0.75); }
  to { filter: brightness(1); }
}

/* The screen transition: new screen only, see App.vue's router-view. */
.screen-enter-active {
  transition: opacity 140ms ease-out;
}

.screen-enter-from {
  opacity: 0;
}
</style>

